/**
 * Offline Storage Utilities
 * 
 * Provides structured storage for offline data using AsyncStorage.
 * Handles serialization, expiration, and cleanup of cached data.
 */

import AsyncStorage from '@react-native-async-storage/async-storage';

// Storage keys
const STORAGE_KEYS = {
  TRIPS: '@tripflow_offline_trips',
  TRIP_DATA: '@tripflow_offline_trip_data',
  EXPENSES: '@tripflow_offline_expenses',
  BOOKINGS: '@tripflow_offline_bookings',
  ITINERARY: '@tripflow_offline_itinerary',
  METADATA: '@tripflow_offline_metadata',
} as const;

export type CachedData<T> = {
  data: T;
  timestamp: number;
  expiresAt?: number;
};

export type OfflineMetadata = {
  lastSync: number;
  version: string;
  tripIds: string[];
};

/**
 * Generic storage operations
 */
class OfflineStorage {
  /**
   * Store data with optional expiration
   */
  async set<T>(key: string, data: T, expiresIn?: number): Promise<void> {
    try {
      const cached: CachedData<T> = {
        data,
        timestamp: Date.now(),
        expiresAt: expiresIn ? Date.now() + expiresIn : undefined,
      };
      
      await AsyncStorage.setItem(key, JSON.stringify(cached));
    } catch (error) {
      console.error(`[OfflineStorage] Error storing data for key ${key}:`, error);
      throw error;
    }
  }

  /**
   * Get data, returns null if expired or not found
   */
  async get<T>(key: string): Promise<T | null> {
    try {
      const stored = await AsyncStorage.getItem(key);
      if (!stored) {
        return null;
      }

      const cached: CachedData<T> = JSON.parse(stored);
      
      // Check expiration
      if (cached.expiresAt && Date.now() > cached.expiresAt) {
        // Data expired, remove it
        await this.remove(key);
        return null;
      }

      return cached.data;
    } catch (error) {
      console.error(`[OfflineStorage] Error retrieving data for key ${key}:`, error);
      return null;
    }
  }

  /**
   * Remove data
   */
  async remove(key: string): Promise<void> {
    try {
      await AsyncStorage.removeItem(key);
    } catch (error) {
      console.error(`[OfflineStorage] Error removing data for key ${key}:`, error);
    }
  }

  /**
   * Check if data exists and is valid
   */
  async has(key: string): Promise<boolean> {
    const data = await this.get(key);
    return data !== null;
  }

  /**
   * Get all keys matching a pattern
   */
  async getAllKeys(prefix?: string): Promise<string[]> {
    try {
      const allKeys = await AsyncStorage.getAllKeys();
      if (prefix) {
        return allKeys.filter(key => key.startsWith(prefix));
      }
      return [...allKeys];
    } catch (error) {
      console.error('[OfflineStorage] Error getting all keys:', error);
      return [];
    }
  }

  /**
   * Clear all offline data
   */
  async clearAll(): Promise<void> {
    try {
      const keys = await this.getAllKeys('@tripflow_offline_');
      await AsyncStorage.multiRemove(keys);
    } catch (error) {
      console.error('[OfflineStorage] Error clearing all data:', error);
    }
  }

  /**
   * Get storage size estimate (approximate)
   */
  async getStorageSize(): Promise<number> {
    try {
      const keys = await this.getAllKeys('@tripflow_offline_');
      let totalSize = 0;
      
      for (const key of keys) {
        const value = await AsyncStorage.getItem(key);
        if (value) {
          // Rough estimate: each character is ~1 byte, JSON adds overhead
          totalSize += value.length * 1.1; // Add 10% for JSON overhead
        }
      }
      
      return totalSize;
    } catch (error) {
      console.error('[OfflineStorage] Error calculating storage size:', error);
      return 0;
    }
  }

  /**
   * Clean up expired data
   */
  async cleanupExpired(): Promise<number> {
    try {
      const keys = await this.getAllKeys('@tripflow_offline_');
      let removed = 0;
      
      for (const key of keys) {
        const data = await this.get(key);
        if (data === null) {
          // Data was expired and removed by get()
          removed++;
        }
      }
      
      return removed;
    } catch (error) {
      console.error('[OfflineStorage] Error cleaning up expired data:', error);
      return 0;
    }
  }

  /**
   * Get cache statistics
   */
  async getCacheStats(): Promise<{
    totalKeys: number;
    totalSize: number;
    expiredCount: number;
  }> {
    try {
      const keys = await this.getAllKeys('@tripflow_offline_');
      const totalSize = await this.getStorageSize();
      const expiredCount = await this.cleanupExpired();
      
      return {
        totalKeys: keys.length,
        totalSize,
        expiredCount,
      };
    } catch (error) {
      console.error('[OfflineStorage] Error getting cache stats:', error);
      return {
        totalKeys: 0,
        totalSize: 0,
        expiredCount: 0,
      };
    }
  }
}

// Singleton instance
export const offlineStorage = new OfflineStorage();

/**
 * Trip-specific storage operations
 */
export const tripStorage = {
  /**
   * Store trip data
   */
  async setTrip(tripId: string, trip: unknown): Promise<void> {
    await offlineStorage.set(`${STORAGE_KEYS.TRIPS}:${tripId}`, trip);
  },

  /**
   * Get trip data
   */
  async getTrip<T>(tripId: string): Promise<T | null> {
    return await offlineStorage.get<T>(`${STORAGE_KEYS.TRIPS}:${tripId}`);
  },

  /**
   * Store complete trip data (trip + related data)
   */
  async setTripData(tripId: string, data: {
    trip: unknown;
    expenses?: unknown[];
    bookings?: unknown[];
    itinerary?: unknown[];
  }): Promise<void> {
    await Promise.all([
      offlineStorage.set(`${STORAGE_KEYS.TRIP_DATA}:${tripId}`, data),
      offlineStorage.set(`${STORAGE_KEYS.TRIPS}:${tripId}`, data.trip),
    ]);
  },

  /**
   * Get complete trip data
   */
  async getTripData<T>(tripId: string): Promise<T | null> {
    return await offlineStorage.get<T>(`${STORAGE_KEYS.TRIP_DATA}:${tripId}`);
  },

  /**
   * Remove trip data
   */
  async removeTrip(tripId: string): Promise<void> {
    await offlineStorage.remove(`${STORAGE_KEYS.TRIPS}:${tripId}`);
    await offlineStorage.remove(`${STORAGE_KEYS.TRIP_DATA}:${tripId}`);
  },

  /**
   * Get all cached trip IDs
   */
  async getCachedTripIds(): Promise<string[]> {
    const keys = await offlineStorage.getAllKeys(STORAGE_KEYS.TRIPS);
    return keys.map(key => key.replace(`${STORAGE_KEYS.TRIPS}:`, ''));
  },
};

/**
 * Metadata operations
 */
export const metadataStorage = {
  /**
   * Update offline metadata
   */
  async updateMetadata(updates: Partial<OfflineMetadata>): Promise<void> {
    const current = await offlineStorage.get<OfflineMetadata>(STORAGE_KEYS.METADATA) || {
      lastSync: 0,
      version: '1.0.0',
      tripIds: [],
    };
    
    const updated: OfflineMetadata = {
      ...current,
      ...updates,
      lastSync: updates.lastSync ?? Date.now(),
    };
    
    await offlineStorage.set(STORAGE_KEYS.METADATA, updated);
  },

  /**
   * Get offline metadata
   */
  async getMetadata(): Promise<OfflineMetadata | null> {
    return await offlineStorage.get<OfflineMetadata>(STORAGE_KEYS.METADATA);
  },
};

// Export storage keys for use in other modules
export { STORAGE_KEYS };

